import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { GeckoMeasurement } from 'types'

export type OverridableParamSource = 'user' | 'scenario'

interface ParamOverride {
  value: GeckoMeasurement | null
  source: OverridableParamSource
}

export interface OverridableParam {
  /** Effective value: `override?.value ?? snapshotBase ?? defaultValue`. */
  value: GeckoMeasurement | null
  /** User-edit setter (tags the override `source: 'user'`). */
  setValue: Dispatch<SetStateAction<GeckoMeasurement | null>>
  /**
   * Scenario-load setter (tags the override `source: 'scenario'`). Typed as a
   * full `Dispatch<SetStateAction<...>>` -- same shape as `setValue` -- so it
   * satisfies `ValueSetterPair`-shaped consumers like `useFfsScenarios`, even
   * though scenario loads only ever call it with a plain value.
   */
  setFromScenario: Dispatch<SetStateAction<GeckoMeasurement | null>>
  /** `null` when the effective value is untouched (snapshot or default). */
  source: OverridableParamSource | null
  snapshotBase: GeckoMeasurement | null
  /** Human-readable source caption for the field, or `undefined` to show none. */
  caption: string | undefined
}

function formatMeasurement(value: GeckoMeasurement): string {
  return `${value.num.toFixed(2)} ${value.unit.name}`
}

export function getParamCaption(
  source: OverridableParamSource | null,
  snapshotBase: GeckoMeasurement | null
): string | undefined {
  if (source === 'scenario') return 'From scenario'
  if (source === 'user') {
    return snapshotBase
      ? `Overridden (asset model: ${formatMeasurement(snapshotBase)})`
      : undefined
  }
  return snapshotBase ? 'From asset model' : undefined
}

/**
 * A snapshot-prefilled measurement param that stays independently editable.
 * Generic over any FFS operating param sourced from the asset-model snapshot
 * (`snapshotBase`).
 *
 * The override is a TAGGED value -- `{ value, source }` -- rather than a
 * plain `useState` seeded from the prop via a sync effect. That means:
 *  - `snapshotBase` arriving late (async component-model fetch) or changing
 *    later doesn't need an effect to "push" it into state -- the effective
 *    value is just `override?.value ?? snapshotBase ?? defaultValue`,
 *    recomputed every render.
 *  - A user edit (`source: 'user'`) keeps winning over a later snapshot
 *    change, because the override isn't touched by `snapshotBase` changing.
 *  - A scenario load (`source: 'scenario'`, via `setFromScenario`) is
 *    distinguishable from a real user edit for the caption.
 *
 * The override resets when `componentId` changes -- the ticket's asset-model
 * component was swapped out, so any prior override no longer applies.
 */
export function useOverridableParam(
  snapshotBase: GeckoMeasurement | null,
  defaultValue: GeckoMeasurement | null,
  componentId: string | undefined
): OverridableParam {
  const [override, setOverride] = useState<ParamOverride | null>(null)

  useEffect(() => {
    setOverride(null)
  }, [componentId])

  const value = override?.value ?? snapshotBase ?? defaultValue

  // Shared apply logic for both setters -- uses the functional `setOverride`
  // form so consecutive calls in the same batch chain off the latest
  // override rather than a stale render's closure.
  const applyOverride = useCallback(
    (
      action: SetStateAction<GeckoMeasurement | null>,
      source: OverridableParamSource
    ) => {
      setOverride((prev) => {
        const prevValue = prev?.value ?? snapshotBase ?? defaultValue
        const nextValue =
          typeof action === 'function'
            ? (
                action as (
                  prev: GeckoMeasurement | null
                ) => GeckoMeasurement | null
              )(prevValue)
            : action
        return { value: nextValue, source }
      })
    },
    [snapshotBase, defaultValue]
  )

  const setValue: Dispatch<SetStateAction<GeckoMeasurement | null>> =
    useCallback((action) => applyOverride(action, 'user'), [applyOverride])

  const setFromScenario: Dispatch<SetStateAction<GeckoMeasurement | null>> =
    useCallback((action) => applyOverride(action, 'scenario'), [applyOverride])

  const source = override?.source ?? null
  const caption = useMemo(
    () => getParamCaption(source, snapshotBase),
    [source, snapshotBase]
  )

  return useMemo(
    () => ({
      value,
      setValue,
      setFromScenario,
      source,
      snapshotBase,
      caption,
    }),
    [value, setValue, setFromScenario, source, snapshotBase, caption]
  )
}
